import { FunctionComponent, KeyboardEvent, ClipboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { faCoin, faTriangleExclamation, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { faLinkedin } from "@fortawesome/free-brands-svg-icons";

import { classNames } from "@/utils/classNames";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";

import { Icon } from "../Icon/Icon";
import { Tooltip } from "../Tooltip/Tooltip";
import { ParsedLinkedinUrl, parseLinkedinUrl } from "./utils";

const MAX_URLS_DEFAULT = 10;
const ENTRY_SEPARATORS = /[\n,;\t]+/;

interface LinkedinUrlChipsInputProps {
	chips: ParsedLinkedinUrl[];
	onChange: (chips: ParsedLinkedinUrl[]) => void;
	maxUrls?: number;
	disabled?: boolean;
}

export const LinkedinUrlChipsInput: FunctionComponent<LinkedinUrlChipsInputProps> = ({
	chips,
	onChange,
	maxUrls = MAX_URLS_DEFAULT,
	disabled = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`companyEnrich.${key}`, options ?? {});
	const { handleCopyClick, renderSnackbar } = useCopyToClipboard();

	const [value, setValue] = useState("");
	const [capError, setCapError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const validCount = chips.filter((c) => c.valid).length;

	const addEntries = (text: string) => {
		if (disabled) {
			return;
		}

		const parts = text
			.split(ENTRY_SEPARATORS)
			.map((s) => s.trim())
			.filter(Boolean);

		if (parts.length === 0) {
			return;
		}

		const next = [...chips];
		let rejectedForCap = 0;

		for (const part of parts) {
			const parsed = parseLinkedinUrl(part);

			if (!parsed) {
				continue;
			}

			if (parsed.valid) {
				if (next.some((c) => c.valid && c.handle === parsed.handle)) {
					continue;
				}

				if (next.filter((c) => c.valid).length >= maxUrls) {
					rejectedForCap++;

					continue;
				}
			} else if (next.some((c) => !c.valid && c.raw === parsed.raw)) {
				continue;
			}

			next.push(parsed);
		}

		setCapError(
			rejectedForCap > 0 ? ts("byUrlCapExceeded", { max: maxUrls, count: rejectedForCap }) : null
		);
		onChange(next);
	};

	const removeChip = (index: number) => {
		const next = chips.filter((_, i) => i !== index);

		if (next.filter((c) => c.valid).length < maxUrls) {
			setCapError(null);
		}

		onChange(next);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();

			if (value.trim()) {
				addEntries(value);
				setValue("");
			}
		} else if (e.key === "Backspace" && !value && chips.length > 0) {
			removeChip(chips.length - 1);
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		addEntries(e.clipboardData.getData("text"));
		setValue("");
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<label className="block text-sm font-medium text-gray-900">{ts("byUrlPasteLabel")}</label>
				<span
					className={classNames(
						"text-xs font-semibold",
						validCount >= maxUrls ? "text-status-error" : "text-gray-500"
					)}
				>
					{validCount} / {maxUrls}
				</span>
			</div>

			<div
				className={classNames(
					"flex flex-wrap gap-1.5 items-start content-start min-h-[78px] max-h-[168px] overflow-y-auto rounded-md border border-border-light p-2 bb-scrollbar-darker",
					disabled ? "opacity-50 pointer-events-none" : "cursor-text"
				)}
				onClick={() => inputRef.current?.focus()}
			>
				{chips.map((chip, index) => (
					<span
						key={chip.valid ? chip.handle : chip.raw}
						className={classNames(
							"inline-flex items-center gap-1 max-w-full rounded-md pl-2 pr-1 py-1 text-[11px] font-medium",
							chip.valid
								? "bg-light-blue text-blue-action"
								: "bg-status-error-light text-status-error"
						)}
					>
						<Tooltip
							content={chip.valid ? "Click to copy" : chip.raw}
							className="flex items-center gap-1"
						>
							<>
								{chip.valid ? (
									<Icon className="shrink-0 w-3 h-3" icon={faLinkedin} />
								) : (
									<Icon className="shrink-0 w-3 h-3" icon={faTriangleExclamation} />
								)}

								<span
									className="truncate max-w-[200px] cursor-pointer hover:underline"
									onClick={(e) => {
										e.stopPropagation();
										handleCopyClick(chip.valid ? chip.url : chip.raw);
									}}
								>
									{chip.valid ? chip.handle : chip.raw}
								</span>
							</>
						</Tooltip>

						<button
							className="shrink-0 flex items-center justify-center w-4 h-4 rounded opacity-70 hover:opacity-100 hover:cursor-pointer"
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								removeChip(index);
							}}
						>
							<Icon className="text-[10px]" icon={faXmark} />
						</button>
					</span>
				))}
				<input
					ref={inputRef}
					autoComplete="off"
					className="flex-1 min-w-[120px] border-none outline-none bg-transparent text-ssm font-normal py-1"
					disabled={disabled}
					placeholder={chips.length === 0 ? ts("byUrlPastePlaceholder") : ""}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onPaste={handlePaste}
				/>
			</div>

			<p className="flex flex-row mt-1.5 text-gray-700 items-center text-sm">
				<span className="mr-1">1x</span>
				<Icon className="!w-[10px] !h-[10px] mr-1 text-gray-700" icon={faCoin} />
				<span> per new person URL</span>
			</p>
			<p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{ts("byUrlPasteHelper")}</p>
			{capError && <p className="text-xs text-status-error mt-1.5 leading-relaxed">{capError}</p>}
			{renderSnackbar()}
		</div>
	);
};
